<template>
  <div class="d-contents">
    <div class="d-flex flex-wrap">
      <form class="find-column" @submit.prevent>
        <input
          v-model="columnSearchTerm"
          type="search"
          class="form-control inline mr-2"
          :placeholder="appTranslate('Find column')"
          :title="appTranslate('Tip: Hold shift to search backwards')"
          @keydown.stop
          @keydown.esc="setFocusedColumn()"
          @keydown.enter="findColumn($event.shiftKey ? reversedDirection : settings.direction)"
        />
      </form>

      <div class="position-relative">
        <button class="btn btn-outline-white" @click="showAdvancedSearch = true">{{ appTranslate('Find data') }}</button>
      </div>
    </div>

    <!--------------------------->
    <!-- Advanced Search Modal -->
    <!--------------------------->

    <Teleport v-if="route.name === 'products' && productsViewStore.view === 'table'" to="#modals">
      <DraggableWindow
        v-if="showAdvancedSearch"
        class="search-modal"
        :style="`max-height: calc(100% - ${windowEdgeMargin * 2}px)`"
        :edge-margin="windowEdgeMargin"
        @close="showAdvancedSearch = false"
      >
        <template #header>
          {{ appTranslate('Find data') }}
        </template>

        <fieldset :class="{ 'disabled-inputs': !!searching }">
          <form class="mb-3" @submit.prevent="findNext(actualDirection)">
            <div>
              <label class="mb-1 d-block">
                {{ appTranslate('Search') }}
                <RegexToggleComponent v-model="store.globalSearchMode" :disabled="!!searching">
                  <!-- s="form-control mt-2 d-flex p-0 ml-0" -->
                  <input
                    ref="inputRef"
                    v-model="dataSearchTerm"
                    type="search"
                    :placeholder="appTranslate('Search for...')"
                    @keydown="keyDownListener"
                    @keyup="keyUpListener"
                  />
                </RegexToggleComponent>
              </label>
            </div>

            <button
              class="link w-fit-content p-0 d-flex align-items-center user-select-none no-outline"
              type="button"
              @click="showMoreSettings = !showMoreSettings"
            >
              <i class="mr-1 fa" style="font-size: 1.5em" :class="[showMoreSettings ? 'fa-angle-down' : 'fa-angle-right']" />
              {{ showMoreSettings ? appTranslate('Show fewer settings') : appTranslate('Show more settings') }}
            </button>

            <div ref="settingsRef" class="form-group settings" :style="settingsStyle">
              <div class="mb-2 settings-group">
                <label>
                  {{ appTranslate('Within') }}
                  <InfoPopupComponent>
                    {{ appTranslate('Can only search within your selected properties') }}
                  </InfoPopupComponent>
                </label>

                <div class="d-flex" style="gap: 0.5em">
                  <label>
                    <input v-model="settings.usedProperties" type="radio" value="selected" />
                    {{ appTranslate('All selected properties') }}
                  </label>

                  <label>
                    <input v-model="settings.usedProperties" type="radio" value="specific-properties" />
                    {{ appTranslate('Specific properties only') }}
                  </label>
                </div>

                <!-- <label>
                <input v-model="bundleCulturalProperties" type="checkbox" />
                {{ appTranslate('Bundle cultural properties') }}
                <InfoPopupComponent content-style="max-width: 400px">
                  {{
                    appTranslate('Treats cultural properties with the same name as a single property, e.g. "DA Description" and "SV Description"')
                  }}</InfoPopupComponent
                >
              </label> -->
                <ImprovedCustomDropdown
                  v-if="settings.usedProperties === 'specific-properties'"
                  v-model="selectedSpecificProperties"
                  :options="propertyOptions"
                  multiple
                  fixed
                />
              </div>

              <label class="d-block">
                {{ appTranslate('Direction') }}
                <div class="d-flex">
                  <select v-model="settings.direction" class="form-control">
                    <option :value="Direction.backwards">{{ appTranslate('Up') }}</option>
                    <option :value="Direction.forwards">{{ appTranslate('Down') }}</option>
                  </select>
                  <button
                    class="link"
                    type="button"
                    @click="settings.direction = settings.direction === Direction.forwards ? Direction.backwards : Direction.forwards"
                  >
                    <div class="d-flex" style="gap: 3px; pointer-events: none">
                      <!-- <i class="fa fa-refresh" /> -->
                      <i class="fa fa-long-arrow-up" :class="{ disabled: settings.direction === Direction.forwards }" />
                      <i class="fa fa-long-arrow-down" :class="{ disabled: settings.direction === Direction.backwards }" />
                    </div>
                  </button>
                </div>
              </label>

              <label class="d-block">
                <input v-model="settings.matchCase" type="checkbox" />
                {{ appTranslate('Match lowercase/UPPERCASE') }}
              </label>

              <!-- <label class="d-block">
                <input v-model="settings.exactMatch" type="checkbox" />
                {{ appTranslate('Match entire cell content') }}
              </label> -->

              <div class="d-block">
                <label>
                  <input v-model="store.globalSearchMode" type="checkbox" true-value="regex" false-value="normal" />
                  {{ appTranslate('Use regular expressions') }}
                </label>

                <a class="ml-2" href="https://sv.wikipedia.org/wiki/Regulj%C3%A4ra_uttryck" target="_blank">
                  <small>{{ appTranslate('Read more') }} <i class="fa fa-external-link" /></small>
                </a>
              </div>
            </div>

            <div class="buttons">
              <button v-if="searchResult" type="button" class="btn btn-secondary" @click="searchResult = undefined">
                {{ appTranslate('Clear') }}
              </button>

              <div class="ml-auto" />

              <div class="position-relative">
                <small class="row-column-text">
                  {{ appTranslate('row: %s, column: %s', [(activeCell?.rowIndex || 0) + 1, (activeCell?.columnIndex || 0) + 1], 'lowercase') }}
                </small>

                <ActionPendingButton
                  type="submit"
                  :pending="searching?.next"
                  :disabled="!!searching || !includedProperties.length"
                  :title="appTranslate('Tip: Hold shift to reverse search direction')"
                >
                  {{ appTranslate(actualDirection === Direction.forwards ? 'Find next' : 'Find previous') }}
                </ActionPendingButton>
              </div>
              <ActionPendingButton
                variant="success"
                type="button"
                :pending="searching?.all"
                :disabled="!!searching || !includedProperties.length"
                :title="`${metaKey} + Enter`"
                @click="findAll()"
              >
                {{ appTranslate('Find all') }}
              </ActionPendingButton>
            </div>
          </form>

          <!-- Messages -->
          <div v-if="searchResult && !searchResult.matches?.length && !searchResult.next">
            {{ appTranslate('No results for') }}
            <em>"{{ searchResult.searchString }}"</em>
          </div>
          <div v-if="errorMsg" class="px-2 mb-2" style="background-color: #f003">
            <small class="text-danger">{{ errorMsg }}</small>
          </div>
          <small v-if="infoMsg" class="text-info">{{ infoMsg }}</small>

          <!-- Search results -->
          <template v-if="searchResult?.matches?.length">
            <div class="d-flex" style="column-gap: 0.5em">
              <strong>{{ appTranslate('Search results for "%s" (%s)', [searchResult.searchString, searchResult.matches.length]) }}</strong>
              <router-link
                class="ml-auto"
                :to="{ name: 'products', query: { _field_sku: searchResultSkus } }"
                :target="bulkStore.editMode ? '_blank' : undefined"
                rel="noopener noreferrer"
                @click="showAdvancedSearch = false"
              >
                {{ appTranslate('Show all results') }}
                <i v-if="bulkStore.editMode" class="fa fa-external-link" />
              </router-link>
            </div>
            <div class="grid-row grid-headers">
              <div>{{ appTranslate('Row') }}</div>
              <div>{{ translate('SKU', 'attributes', 'capitalize') }}</div>
              <div>{{ appTranslate('Property') }}</div>
              <div>{{ appTranslate('Matching value') }}</div>
            </div>

            <div class="search-results">
              <div
                v-for="(entry, i) of extendedSearchResults"
                :key="i"
                class="grid-row grid-data-row"
                :class="{ focused: focusedEntry === i, 'disabled-entry': entry.disabled }"
                :title="entry.disabled ? appTranslate('Cannot visit cell since the property is not visible') : undefined"
                @click="ensureProductAndVisit(entry.index, entry.propertyIndex, i)"
              >
                <div>{{ entry.index + 1 }}</div>
                <MoreReadableSkuComponent :sku="entry.sku" :is-temporary-sku="entry.isTemporarySku" />
                <div>{{ entry.property?.header }}</div>
                <div v-html="entry.valueHtml" />
              </div>
            </div>
          </template>
        </fieldset>
      </DraggableWindow>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import ActionPendingButton from '@/components/ActionPendingButton.vue'
import DraggableWindow from '@/components/DraggableWindow.vue'
import ImprovedCustomDropdown from '@/components/ImprovedCustomDropdown.vue'
import InfoPopupComponent from '@/components/simple/InfoPopupComponent.vue'
import MoreReadableSkuComponent from '@/components/simple/MoreReadableSkuComponent.vue'
import RegexToggleComponent from '@/components/simple/RegexToggleComponent.vue'
import { useTableSearch } from '@/composables/useTableSearch'
import { useTranslation } from '@/composables/useTranslation'
import { Cell } from '@/models/GenericInterfaces'
import { Direction, Option } from '@/models/Global'
import { DataSearchResult } from '@/models/Search'
import DataService from '@/services/DataService'
import { useDefaultStore } from '@/store'
import { useProductsStore } from '@/store/productsArchiveStore'
import { useProductsBulkStore } from '@/store/productsBulkStore'
import { useProductsViewStore } from '@/store/productsViewStore'
import { isInfoTextProperty } from '@/utils/propertyUtils'
import { isNumeric, metaKey, toComparable, toNumber, toSafeString } from '@/utils/tools'
import { computed, nextTick, onActivated, reactive, ref, StyleValue, watch } from 'vue'
import { useRoute } from 'vue-router'

// -----------
// -- Setup --
// -----------
const props = defineProps<{ activeCell?: Cell; focusedColumn?: number }>()
const emit = defineEmits(['visit', 'update:focusedColumn'])

const store = useDefaultStore()
const productsStore = useProductsStore()
const productsViewStore = useProductsViewStore()
const bulkStore = useProductsBulkStore()
const { translate, appTranslate } = useTranslation()
const route = useRoute()

const { setSearchStartingPoint, findHeader, findData } = useTableSearch()

const scrollMarginX = 225
const scrollMarginY = 26 * 4
const windowEdgeMargin = 3

// ---------------
// -- Reactives --
// ---------------
const columnSearchTerm = ref('')
const dataSearchTerm = ref('')

const showAdvancedSearch = ref(false)
const showMoreSettings = ref(false)
const settingsRef = ref<HTMLElement>()
const settingsStyle = ref<StyleValue>()
const searchResult = ref<DataSearchResult | undefined>()
const shiftIsPressed = ref(false)
const searching = ref<{ all?: boolean; next?: boolean }>()
const focusedEntry = ref<number>()
const selectedSpecificProperties = ref<string[]>()

const errorMsg = ref<string>()
const infoMsg = ref<string>()

const settings = reactive<{
  direction: Direction
  matchCase?: boolean
  usedProperties: 'selected' | 'specific-properties'
  exactMatch?: boolean
}>({ usedProperties: 'selected', direction: Direction.forwards })
// const bundleCulturalProperties = ref(false)

const inputRef = ref<HTMLInputElement>()

// --------------------
// -- Computed props --
// --------------------
const propertyOptions = computed(() => {
  // const added = new Set<string>()

  return (
    Array.from(bulkStore.combinedProperties)
      // return Array.from(bulkStore.nonDefaultCulturalProperties || [])
      ?.sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0))
      .reduce<Option<string>[]>((acc, property) => {
        // if (!isInfoTextProperty(property) && (!bundleCulturalProperties.value || !added.has(property.defaultName))) {
        // const option = bundleCulturalProperties.value
        //   ? { displayValue: property.name || property.defaultName, value: property.defaultName }
        //   : {
        //       displayValue: property.header || property.defaultHeader,
        //       value: property.defaultHeader,
        //     }
        // added.add(property.defaultName)
        if (!isInfoTextProperty(property)) {
          acc.push({
            displayValue: property.header || property.defaultHeader,
            value: property.defaultHeader,
          })
        }
        return acc
      }, [])
  )
})

const includedProperties = computed(() => {
  if (settings.usedProperties === 'specific-properties') {
    // const selectedSpecificProperties = new Set(specificProperties.value)
    // filtering info text for now since it isn't included in the backend search
    const selectedSpecificPropertiesSet = new Set(selectedSpecificProperties.value)
    // if (bundleCulturalProperties.value) {
    //   return bulkStore.combinedProperties.filter((property) => selectedSpecificPropertiesSet.has(property.defaultName) && !isInfoTextProperty(property))
    // }
    return bulkStore.combinedProperties.filter((property) => selectedSpecificPropertiesSet.has(property.defaultHeader) && !isInfoTextProperty(property))
    // return (
    //   bulkStore.nonDefaultCulturalProperties?.filter(
    //     (property) => selectedSpecificPropertiesSet.has(property.defaultHeader) && !isInfoTextProperty(property),
    //   ) || []
    // )
    // return propertyOptions.value.filter((option) => option.value && specificPropertiesSet.has(option.value))
  }
  return bulkStore.combinedProperties
})

// const selectedSpecificPropertiesModel = computed({
//   get: () => includedProperties.value.map((p) => (bundleCulturalProperties.value ? p.defaultName : p.defaultHeader)),
//   set: (value) => {
//     selectedSpecificProperties.value = value
//   },
// })

// onMounted(() => {
//   DataService.loadProperties()
// })

const isRegexSearch = computed(() => store.globalSearchMode === 'regex')

const extendedSearchResults = computed(() => {
  if (searchResult.value) {
    const searchValue = isNumeric(searchResult.value.searchString) ? toNumber(searchResult.value.searchString)?.toString() : searchResult.value.searchString

    return searchResult.value.matches?.flatMap((entry) => {
      // const foundProperty = bulkStore.combinedProperties[entry.propertyIndex]
      const comparableProperty = toComparable(entry.property.defaultHeader)
      const visibleProperty = bulkStore.combinedPropertiesByComparable[comparableProperty]
      const foundProperty = bulkStore.nonDefaultCulturalPropertiesByComparable[comparableProperty]

      return {
        ...entry,
        propertyIndex: visibleProperty?.index ?? -1,
        disabled: !visibleProperty,
        property: foundProperty,
        valueHtml: toSafeString(entry.matchingValue).replace(new RegExp(searchValue, searchResult.value?.matchCase ? 'gm' : 'ig'), (substring) => {
          return `<span class="match">${substring}</span>`
        }),
      }
    })
  }
  return undefined
})

const reversedDirection = computed(() => (settings.direction === Direction.forwards ? Direction.backwards : Direction.forwards))

const actualDirection = computed(() => (shiftIsPressed.value ? reversedDirection.value : settings.direction))

const searchResultSkus = computed(() => {
  const uniqueSkus = new Set(searchResult.value?.matches?.map((match) => match.sku))
  return Array.from(uniqueSkus).join(',')
})

// --------------
// -- Watchers --
// --------------
// watch(
//   () => store.translationLanguage,
//   () => {
//     fieldSearchVars.value = { matchIndex: -1, search: '' }
//   },
// )

watch(
  () => props.activeCell,
  () => {
    setSearchStartingPoint(props.activeCell)
  },
)

watch([() => store.dataCulture, () => store.includedCountries, () => route.fullPath, () => bulkStore.selectedProperties], () => {
  searchResult.value = undefined
})

watch(
  [() => settings.usedProperties, showMoreSettings, includedProperties],
  async () => {
    await nextTick()
    settingsStyle.value = { height: showMoreSettings.value ? `${settingsRef.value?.scrollHeight}px` : '0' }
  },
  { immediate: true },
)

watch(showAdvancedSearch, async (activated) => {
  if (activated) {
    await nextTick()
    inputRef.value?.focus()
  }
})

// ---------------
// -- Lifecycle --
// ---------------
onActivated(() => {
  focusedEntry.value = undefined
})

const keyDownListener = (e: KeyboardEvent) => {
  shiftIsPressed.value = e.shiftKey

  if (e.key !== 'Escape' || dataSearchTerm.value) {
    e.stopPropagation()
  }

  if (e.key === 'Enter') {
    if ((e.ctrlKey || e.metaKey) && includedProperties.value.length) {
      findAll()
    } else {
      findNext(actualDirection.value, true)
      e.preventDefault()
    }
  }
}

const keyUpListener = (e: KeyboardEvent) => {
  shiftIsPressed.value = e.shiftKey
}

// ---------------
// -- Functions --
// ---------------

const findColumn = (direction: Direction) => {
  const foundColumn = findHeader(columnSearchTerm.value, direction, bulkStore.combinedProperties)
  if (foundColumn) {
    setFocusedColumn(foundColumn)
    emit('visit', { cell: { columnIndex: foundColumn }, scrollMarginX, scrollBehaviour: 'smooth' })
  }
}

const setFocusedColumn = (column?: number) => {
  emit('update:focusedColumn', column)
}

const findAll = async () => {
  try {
    if (!searching.value) {
      resetAll()
      if (dataSearchTerm.value) {
        searching.value = { all: true }
        searchResult.value = await DataService.findProductData(
          dataSearchTerm.value,
          route.query,
          undefined,
          undefined,
          undefined,
          bulkStore.combinedProperties,
          includedProperties.value,
          true,
          isRegexSearch.value,
          settings.matchCase,
          settings.exactMatch,
          bulkStore.productInputBySku,
          bulkStore.extraProducts,
          bulkStore.newProducts,
          productsStore.expiredProducts,
        )
      } else {
        errorMsg.value = appTranslate('Search must contain at least one character')
      }
    }
  } finally {
    searching.value = undefined
  }
}

const findNext = async (direction: Direction, focusInputFieldAfter = true) => {
  try {
    if (!searching.value) {
      if (dataSearchTerm.value) {
        resetAll()
        const searchResult = await findData(dataSearchTerm.value, direction, includedProperties.value, false, isRegexSearch.value, settings.matchCase)
        if (searchResult?.next) {
          const columnIndex = bulkStore.combinedPropertiesByComparable[toComparable(searchResult.next.property.defaultHeader)]?.index

          if (columnIndex !== undefined) {
            return ensureProductAndVisit(searchResult.next.index, columnIndex)
          }
        }

        setSearchStartingPoint(props.activeCell)

        const position = translate(direction === -1 ? 'before' : 'after', 'application', 'lowercase')

        const columnString = props.activeCell
          ? `${translate('column', 'application', 'lowercase')} "${bulkStore.combinedProperties?.[props.activeCell.columnIndex]?.header}" ${translate('on', 'application', 'lowercase')}`
          : ''

        const rowString = `${translate('row', 'application', 'lowercase')} ${props.activeCell ? props.activeCell.rowIndex + 1 : 1}`

        infoMsg.value = appTranslate(`No more results found for %s`, [`"${dataSearchTerm.value}" ${position} ${columnString} ${rowString}`])
      }
    } else {
      errorMsg.value = appTranslate('Search must contain at least one character')
    }
  } finally {
    searching.value = undefined

    if (focusInputFieldAfter) {
      // makeshift solution since input field loses focus after search for some reason (connected to selectionData)
      inputRef.value?.blur()
      setTimeout(() => {
        inputRef.value?.focus()
      })
    }
  }
}

const ensureProductAndVisit = async (rowIndex: number, columnIndex: number, focusResultRow?: number) => {
  if (columnIndex >= 0) {
    const index = Math.min(rowIndex, productsStore.currentTotalProducts - 1)
    if (!productsStore.currentProducts[index]) {
      // insert empty row to allow scroll (will trigger fetch if in view)
      productsStore.currentProducts[index] = undefined
    }

    await nextTick()
    const cell = { rowIndex, columnIndex }

    focusedEntry.value = focusResultRow
    visitCell(cell)
  }
}

const visitCell = (cell: Cell) => {
  emit('visit', { cell, scrollMarginX, scrollMarginY })
}

const resetAll = () => {
  infoMsg.value = undefined
  errorMsg.value = undefined
  focusedEntry.value = undefined
  searchResult.value = undefined
}
</script>

<style lang="scss" scoped>
:deep() {
  .match {
    color: blue;
    font-weight: bold;
  }
}

.find-column,
.find-data {
  button {
    border-radius: 3px;
    border: 1px solid #aaa;
  }
}

.find-data {
  .from {
    position: absolute;
    top: 0;
    right: 5px;
    color: var(--disabled);
    font-size: 10px;
  }
}

.search-modal {
  width: min(500px, 100%);

  fieldset {
    display: flex;
    flex-direction: column;

    &.disabled-inputs {
      label,
      input,
      button {
        pointer-events: none;
        color: var(--disabled);
      }

      input {
        background-color: #e9ecef;
      }
    }
  }

  .disabled-input {
    pointer-events: none;
    background-color: #e9ecef;
  }

  label {
    user-select: none;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 0.5em;

    .fifty-fifty {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1em;
    }

    .buttons {
      display: flex;
      // justify-content: end;
      gap: 0.5em;
    }
  }

  .settings {
    overflow: hidden;
    transition: height 0.3s ease;

    .settings-group {
      background-color: #eee;
      padding: 0.5em;
      border-radius: 0.25em;
      // border: 1px solid #ddd;
    }
  }

  .row-column-text {
    position: absolute;
    display: flex;
    margin-top: -1.5em;
    width: max-content;
  }

  .search-results {
    overflow: auto;
    overscroll-behavior: none;
    max-height: 350px;
  }

  .grid-row {
    display: grid;
    grid-template-columns: 50px 100px 1fr 2fr;

    > * {
      // max-height: 150px;
      // overflow: auto;
      padding: 0.25em;
    }

    &.grid-headers {
      // $modal-padding: 0.5em;
      // position: sticky;
      // margin-top: $modal-padding;
      // top: 0;
      z-index: 1;
      border-radius: 3px;

      // background-color: var(--theme-bg);
      // color: var(--theme-header-colour);
      background-color: var(--primary);
      color: white;

      font-weight: bold;

      // &::before {
      //   content: '';
      //   position: absolute;
      //   width: 100%;
      //   height: $modal-padding;
      //   transform: translateY(-100%);
      //   background-color: white;
      // }
    }

    &.grid-data-row {
      cursor: pointer;
      border-bottom: 2px solid white;

      &:hover:not(.disabled-entry) > * {
        background-color: #e5e5e5;
      }

      > * {
        background-color: #f0f0f0;
        word-break: break-word;
      }

      &.focused > * {
        background-color: #dadada;
      }

      &.disabled-entry {
        cursor: default;
        color: #aaa;
      }
    }
  }
}
</style>
